// frontend/src/views/PatientProfile.tsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  FileText,
  Save,
  Pencil,
  Plus,
  Eye,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useDocuments } from "../contexts/DocumentsContext";
import {
  PatientData,
  getPatient,
  updatePatient,
  Document,
} from "../services/api";

type TabType = "diagnosi" | "relazioni" | "consulti" | "note";

const DocumentTab = React.memo(
  ({
    doc,
    onNavigate,
    activeTab,
  }: {
    doc: Document;
    onNavigate: (id: number) => void;
    activeTab: TabType;
  }) => (
    <div className="py-3 flex justify-between items-center">
      <div>
        <h4 className="font-medium">{doc.title}</h4>
        <p className="text-sm text-gray-500">
          {new Date(doc.exam_date).toLocaleDateString("it-IT")}
        </p>
      </div>
      <button
        onClick={() => onNavigate(doc.id)}
        className="text-gray-600 hover:text-gray-900"
      >
        <Eye size={20} />
      </button>
    </div>
  ),
  (prevProps, nextProps) => {
    return (
      prevProps.doc.id === nextProps.doc.id &&
      prevProps.activeTab === nextProps.activeTab
    );
  }
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  isEditable?: boolean;
  isEditing?: boolean;
  onEditToggle?: () => void;
}

const Section: React.FC<SectionProps> = ({
  title,
  children,
  icon,
  defaultOpen = false,
  isEditable = true,
  isEditing = false,
  onEditToggle,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { user } = useAuth();

  return (
    <div className="mb-4 bg-white rounded-lg shadow-sm border">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-600">{icon}</span>}
          <span className="font-medium">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {user?.isAdmin && isEditable && (
            <button
              onClick={onEditToggle}
              className="p-2 text-gray-600 hover:text-gray-900"
            >
              {isEditing ? <Save size={16} /> : <Pencil size={16} />}
            </button>
          )}
          <button onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {isOpen && <div className="px-4 py-3 border-t">{children}</div>}
    </div>
  );
};

interface EditingSections {
  anagrafica: boolean;
  allergie: boolean;
  anamnesi: boolean;
  patologie: boolean;
  terapie: boolean;
}

const PatientProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    documents,
    refreshDocuments,
    sortDirection,
    setSortDirection,
    setNeedsUpdates,
  } = useDocuments();

  useEffect(() => {
    setNeedsUpdates(true);
    return () => setNeedsUpdates(false);
  }, [setNeedsUpdates]);

  const [activeTab, setActiveTab] = useState<TabType>(
    () => (location.state?.activeTab ?? "relazioni") as TabType
  );

  type TabMapping = Record<TabType, string[]>;

  const tabTagMapping = useMemo<TabMapping>(
    () => ({
      relazioni: ["relazione", "relazioni"],
      diagnosi: ["diagnosi"],
      consulti: ["consulto", "consulti"],
      note: ["nota", "note"],
    }),
    []
  );

  const handleTabChange = useCallback((newTab: TabType) => {
    setActiveTab(newTab);
  }, []);

  const handleSortToggle = useCallback(() => {
    setSortDirection((prev) => (prev === "DESC" ? "ASC" : "DESC"));
  }, [setSortDirection]);

  const handleNavigate = useCallback(
    (docId: number) => {
      navigate(`/documents/${docId}`, {
        state: { returnTo: "/patient", activeTab },
      });
    },
    [navigate, activeTab]
  );

  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc) =>
        doc.tags.some((tag) =>
          tabTagMapping[activeTab].includes(tag.toLowerCase())
        )
      ),
    [documents, activeTab]
  );

  const [editingSections, setEditingSections] = useState<EditingSections>({
    anagrafica: false,
    allergie: false,
    anamnesi: false,
    patologie: false,
    terapie: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientData, setPatientData] = useState<PatientData | null>(null);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getPatient();
        setPatientData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.error?.message || "Error loading patient data"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, []);

  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("it-IT");
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const handleSectionEdit = async (section: keyof EditingSections) => {
    if (editingSections[section]) {
      try {
        setIsLoading(true);
        setError(null);

        const sectionData: Partial<PatientData> = {};
        switch (section) {
          case "allergie":
            sectionData.allergie = patientData!.allergie;
            break;
          case "anamnesi":
            sectionData.anamnesi_familiare = patientData!.anamnesi_familiare;
            break;
          case "patologie":
            sectionData.patologie = patientData!.patologie;
            break;
          case "terapie":
            sectionData.terapie = patientData!.terapie;
            break;
          case "anagrafica":
            sectionData.nome = patientData!.nome;
            sectionData.cognome = patientData!.cognome;
            sectionData.data_nascita = patientData!.data_nascita;
            sectionData.codice_fiscale = patientData!.codice_fiscale;
            sectionData.telefono = patientData!.telefono;
            sectionData.email = patientData!.email;
            sectionData.medico_base = patientData!.medico_base;
            sectionData.gruppo_sanguigno = patientData!.gruppo_sanguigno;
            break;
        }

        await updatePatient(sectionData);

        const response = await getPatient();
        setPatientData(response.data);
        setEditingSections((prev) => ({ ...prev, [section]: false }));
      } catch (err: any) {
        setError(err.message || "Error saving changes");
      } finally {
        setIsLoading(false);
      }
    } else {
      setEditingSections((prev) => ({ ...prev, [section]: true }));
    }
  };

  const handlePatientDataChange = (field: string, value: any) => {
    if (patientData) {
      setPatientData({ ...patientData, [field]: value });
    }
  };

  if (isLoading && !patientData) {
    return (
      <div className="max-w-4xl mx-auto p-6 w-full">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
          <div className="space-y-4">
            <div className="h-24 bg-gray-200 rounded-lg"></div>
            <div className="h-24 bg-gray-200 rounded-lg"></div>
            <div className="h-24 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!patientData) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 w-full">
      <div className="p-6 mb-6 bg-white rounded-lg shadow-sm border">
        <div className="flex justify-between items-start mb-4">
          <div>
            {editingSections.anagrafica ? (
              <div>
                <div className="flex gap-2 mb-1">
                  <input
                    type="text"
                    value={patientData.nome}
                    onChange={(e) =>
                      handlePatientDataChange("nome", e.target.value)
                    }
                    className="text-2xl font-bold px-2 py-1 border rounded"
                    placeholder="Nome"
                  />
                  <input
                    type="text"
                    value={patientData.cognome}
                    onChange={(e) =>
                      handlePatientDataChange("cognome", e.target.value)
                    }
                    className="text-2xl font-bold px-2 py-1 border rounded"
                    placeholder="Cognome"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={formatDateForInput(patientData.data_nascita)}
                    onChange={(e) =>
                      handlePatientDataChange("data_nascita", e.target.value)
                    }
                    className="px-2 py-1 border rounded text-gray-500"
                  />
                  <input
                    type="text"
                    value={patientData.codice_fiscale}
                    onChange={(e) =>
                      handlePatientDataChange("codice_fiscale", e.target.value)
                    }
                    className="px-2 py-1 border rounded text-gray-500"
                    placeholder="Codice Fiscale"
                  />
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold mb-1">
                  {patientData.nome} {patientData.cognome}
                </h1>
                <p className="text-gray-500">
                  {formatDateForDisplay(patientData.data_nascita)} ·{" "}
                  {patientData.codice_fiscale}
                </p>
              </>
            )}
          </div>
          {user?.isAdmin && (
            <button
              onClick={() => handleSectionEdit("anagrafica")}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              {editingSections.anagrafica ? "Salva" : "Modifica"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Contatti</h3>
            {editingSections.anagrafica ? (
              <>
                <input
                  type="tel"
                  value={patientData.telefono}
                  onChange={(e) =>
                    handlePatientDataChange("telefono", e.target.value)
                  }
                  className="text-sm w-full px-2 py-1 border rounded mb-1"
                  placeholder="Telefono"
                />
                <input
                  type="email"
                  value={patientData.email}
                  onChange={(e) =>
                    handlePatientDataChange("email", e.target.value)
                  }
                  className="text-sm w-full px-2 py-1 border rounded"
                  placeholder="Email"
                />
              </>
            ) : (
              <>
                <p className="text-sm">{patientData.telefono}</p>
                <p className="text-sm">{patientData.email}</p>
              </>
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Info Mediche
            </h3>
            {editingSections.anagrafica ? (
              <>
                <div className="mb-1">
                  <label className="text-xs text-gray-500">
                    Medico di base
                  </label>
                  <input
                    type="text"
                    value={patientData.medico_base}
                    onChange={(e) =>
                      handlePatientDataChange("medico_base", e.target.value)
                    }
                    className="text-sm w-full px-2 py-1 border rounded"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">
                    Gruppo sanguigno
                  </label>
                  <input
                    type="text"
                    value={patientData.gruppo_sanguigno}
                    onChange={(e) =>
                      handlePatientDataChange(
                        "gruppo_sanguigno",
                        e.target.value
                      )
                    }
                    className="text-sm w-full px-2 py-1 border rounded"
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-sm">
                  Medico di base: {patientData.medico_base}
                </p>
                <p className="text-sm">
                  Gruppo sanguigno: {patientData.gruppo_sanguigno}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <Section
        title="Allergie e Intolleranze"
        icon={<AlertTriangle size={16} />}
        isEditable={true}
        isEditing={editingSections.allergie}
        onEditToggle={() => handleSectionEdit("allergie")}
      >
        <div className="space-y-3">
          {editingSections.allergie ? (
            <>
              {(patientData?.allergie || []).map((allergia, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 rounded-lg flex items-center gap-3"
                >
                  <button
                    onClick={() => {
                      const newAllergie = patientData.allergie.filter(
                        (_, i) => i !== index
                      );
                      handlePatientDataChange("allergie", newAllergie);
                    }}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={allergia.nome}
                      onChange={(e) => {
                        const newAllergie = [...patientData.allergie];
                        newAllergie[index] = {
                          ...allergia,
                          nome: e.target.value,
                        };
                        handlePatientDataChange("allergie", newAllergie);
                      }}
                      className="flex-1 px-2 py-1 border rounded"
                      placeholder="Nome allergia"
                    />
                    <select
                      value={allergia.tipo}
                      onChange={(e) => {
                        const newAllergie = [...patientData.allergie];
                        newAllergie[index] = {
                          ...allergia,
                          tipo: e.target.value,
                        };
                        handlePatientDataChange("allergie", newAllergie);
                      }}
                      className="px-2 py-1 border rounded"
                    >
                      <option value="Farmaco">Farmaco</option>
                      <option value="Alimento">Alimento</option>
                      <option value="Ambientale">Ambientale</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    value={allergia.note}
                    onChange={(e) => {
                      const newAllergie = [...patientData.allergie];
                      newAllergie[index] = {
                        ...allergia,
                        note: e.target.value,
                      };
                      handlePatientDataChange("allergie", newAllergie);
                    }}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Note"
                  />
                </div>
              ))}

              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    const newAllergie = [
                      ...patientData.allergie,
                      {
                        tipo: "Farmaco",
                        nome: "",
                        note: "",
                      },
                    ];
                    handlePatientDataChange("allergie", newAllergie);
                  }}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded-lg hover:bg-gray-50"
                >
                  <Plus size={16} />
                  <span>Aggiungi allergia</span>
                </button>
              </div>
            </>
          ) : (
            patientData?.allergie?.map((allergia, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between mb-1">
                  <span className="font-medium">{allergia.nome}</span>
                  <span className="text-sm text-gray-500">{allergia.tipo}</span>
                </div>
                {allergia.note && (
                  <p className="text-sm text-gray-600">{allergia.note}</p>
                )}
              </div>
            ))
          )}
        </div>
      </Section>

      <Section
        title="Anamnesi Familiare"
        icon={<FileText size={16} className="text-gray-600" />}
        isEditable={true}
        isEditing={editingSections.anamnesi}
        onEditToggle={() => handleSectionEdit("anamnesi")}
      >
        <div className="space-y-3">
          {editingSections.anamnesi ? (
            <>
              {(patientData?.anamnesi_familiare || []).map((item, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 rounded-lg flex items-center gap-3"
                >
                  <button
                    onClick={() => {
                      const newAnamnesi = patientData.anamnesi_familiare.filter(
                        (_, i) => i !== index
                      );
                      handlePatientDataChange(
                        "anamnesi_familiare",
                        newAnamnesi
                      );
                    }}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                  <div className="flex-1">
                    <div className="flex gap-2 mb-1">
                      <input
                        type="text"
                        value={item.grado}
                        onChange={(e) => {
                          const newAnamnesi = [
                            ...patientData.anamnesi_familiare,
                          ];
                          newAnamnesi[index] = {
                            ...item,
                            grado: e.target.value,
                          };
                          handlePatientDataChange(
                            "anamnesi_familiare",
                            newAnamnesi
                          );
                        }}
                        className="flex-1 px-2 py-1 border rounded"
                        placeholder="Grado parentela"
                      />
                      <input
                        type="text"
                        value={item.patologia}
                        onChange={(e) => {
                          const newAnamnesi = [
                            ...patientData.anamnesi_familiare,
                          ];
                          newAnamnesi[index] = {
                            ...item,
                            patologia: e.target.value,
                          };
                          handlePatientDataChange(
                            "anamnesi_familiare",
                            newAnamnesi
                          );
                        }}
                        className="flex-1 px-2 py-1 border rounded"
                        placeholder="Patologia"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    const newAnamnesi = [
                      ...patientData.anamnesi_familiare,
                      {
                        grado: "",
                        patologia: "",
                      },
                    ];
                    handlePatientDataChange("anamnesi_familiare", newAnamnesi);
                  }}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded-lg hover:bg-gray-50"
                >
                  <Plus size={16} />
                  <span>Aggiungi familiare</span>
                </button>
              </div>
            </>
          ) : (
            patientData?.anamnesi_familiare?.map((item, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between">
                  <span className="font-medium">{item.grado}</span>
                  <span className="text-gray-600">{item.patologia}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </Section>

      <Section
        title="Patologie"
        icon={<FileText size={16} className="text-gray-600" />}
        defaultOpen={true}
        isEditable={true}
        isEditing={editingSections.patologie}
        onEditToggle={() => handleSectionEdit("patologie")}
      >
        <div className="space-y-3">
          {editingSections.patologie ? (
            <>
              {(patientData?.patologie || []).map((patologia, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 rounded-lg flex items-center gap-3"
                >
                  <button
                    onClick={() => {
                      const newPatologie = patientData.patologie.filter(
                        (_, i) => i !== index
                      );
                      handlePatientDataChange("patologie", newPatologie);
                    }}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={patologia.nome}
                        onChange={(e) => {
                          const newPatologie = [...patientData.patologie];
                          newPatologie[index] = {
                            ...patologia,
                            nome: e.target.value,
                          };
                          handlePatientDataChange("patologie", newPatologie);
                        }}
                        className="flex-1 px-2 py-1 border rounded"
                        placeholder="Nome patologia"
                      />
                      <select
                        value={patologia.stato}
                        onChange={(e) => {
                          const newPatologie = [...patientData.patologie];
                          newPatologie[index] = {
                            ...patologia,
                            stato: e.target.value as "In corso" | "Risolta",
                          };
                          handlePatientDataChange("patologie", newPatologie);
                        }}
                        className="px-2 py-1 border rounded"
                      >
                        <option value="In corso">In corso</option>
                        <option value="Risolta">Risolta</option>
                      </select>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="date"
                        value={patologia.data}
                        onChange={(e) => {
                          const newPatologie = [...patientData.patologie];
                          newPatologie[index] = {
                            ...patologia,
                            data: e.target.value,
                          };
                          handlePatientDataChange("patologie", newPatologie);
                        }}
                        className="flex-1 px-2 py-1 border rounded text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={patologia.note}
                      onChange={(e) => {
                        const newPatologie = [...patientData.patologie];
                        newPatologie[index] = {
                          ...patologia,
                          note: e.target.value,
                        };
                        handlePatientDataChange("patologie", newPatologie);
                      }}
                      className="w-full px-2 py-1 border rounded text-sm"
                      placeholder="Note"
                    />
                  </div>
                </div>
              ))}

              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    const newPatologie = [
                      ...patientData.patologie,
                      {
                        nome: "",
                        data: new Date().toISOString().split("T")[0],
                        stato: "In corso" as const,
                        note: "",
                      },
                    ];
                    handlePatientDataChange("patologie", newPatologie);
                  }}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded-lg hover:bg-gray-50"
                >
                  <Plus size={16} />
                  <span>Aggiungi patologia</span>
                </button>
              </div>
            </>
          ) : (
            patientData?.patologie?.map((patologia, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between mb-1">
                  <span className="font-medium">{patologia.nome}</span>
                  <span
                    className={`text-sm px-2 py-0.5 rounded ${
                      patologia.stato === "In corso"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {patologia.stato}
                  </span>
                </div>
                <div className="text-sm text-gray-500 mb-1">
                  {new Date(patologia.data).toLocaleDateString("it-IT")}
                </div>
                {patologia.note && (
                  <p className="text-sm text-gray-600">{patologia.note}</p>
                )}
              </div>
            ))
          )}
        </div>
      </Section>

      <Section
        title="Terapie in Corso"
        icon={<FileText size={16} className="text-gray-600" />}
        defaultOpen={true}
        isEditable={true}
        isEditing={editingSections.terapie}
        onEditToggle={() => handleSectionEdit("terapie")}
      >
        <div className="space-y-3">
          {editingSections.terapie ? (
            <>
              {(patientData?.terapie || []).map((terapia, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 rounded-lg flex items-center gap-3"
                >
                  <button
                    onClick={() => {
                      const newTerapie = patientData.terapie.filter(
                        (_, i) => i !== index
                      );
                      handlePatientDataChange("terapie", newTerapie);
                    }}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={terapia.farmaco}
                        onChange={(e) => {
                          const newTerapie = [...patientData.terapie];
                          newTerapie[index] = {
                            ...terapia,
                            farmaco: e.target.value,
                          };
                          handlePatientDataChange("terapie", newTerapie);
                        }}
                        className="flex-1 px-2 py-1 border rounded"
                        placeholder="Nome farmaco"
                      />
                      <input
                        type="text"
                        value={terapia.dosaggio}
                        onChange={(e) => {
                          const newTerapie = [...patientData.terapie];
                          newTerapie[index] = {
                            ...terapia,
                            dosaggio: e.target.value,
                          };
                          handlePatientDataChange("terapie", newTerapie);
                        }}
                        className="w-32 px-2 py-1 border rounded"
                        placeholder="Dosaggio"
                      />
                    </div>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={terapia.frequenza}
                        onChange={(e) => {
                          const newTerapie = [...patientData.terapie];
                          newTerapie[index] = {
                            ...terapia,
                            frequenza: e.target.value,
                          };
                          handlePatientDataChange("terapie", newTerapie);
                        }}
                        className="flex-1 px-2 py-1 border rounded text-sm"
                        placeholder="Frequenza (es: 1 volta/die)"
                      />
                      <input
                        type="date"
                        value={terapia.inizioTerapia}
                        onChange={(e) => {
                          const newTerapie = [...patientData.terapie];
                          newTerapie[index] = {
                            ...terapia,
                            inizioTerapia: e.target.value,
                          };
                          handlePatientDataChange("terapie", newTerapie);
                        }}
                        className="w-40 px-2 py-1 border rounded text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={terapia.prescrittore}
                      onChange={(e) => {
                        const newTerapie = [...patientData.terapie];
                        newTerapie[index] = {
                          ...terapia,
                          prescrittore: e.target.value,
                        };
                        handlePatientDataChange("terapie", newTerapie);
                      }}
                      className="w-full px-2 py-1 border rounded text-sm"
                      placeholder="Prescritto da"
                    />
                  </div>
                </div>
              ))}

              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    const newTerapie = [
                      ...patientData.terapie,
                      {
                        farmaco: "",
                        dosaggio: "",
                        frequenza: "",
                        inizioTerapia: new Date().toISOString().split("T")[0],
                        prescrittore: "",
                      },
                    ];
                    handlePatientDataChange("terapie", newTerapie);
                  }}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded-lg hover:bg-gray-50"
                >
                  <Plus size={16} />
                  <span>Aggiungi terapia</span>
                </button>
              </div>
            </>
          ) : (
            patientData?.terapie?.map((terapia, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between mb-1">
                  <span className="font-medium">{terapia.farmaco}</span>
                  <span className="text-sm text-gray-500">
                    Inizio:{" "}
                    {new Date(terapia.inizioTerapia).toLocaleDateString(
                      "it-IT"
                    )}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {terapia.dosaggio} - {terapia.frequenza}
                </p>
                <p className="text-sm text-gray-500">
                  Prescritto da: {terapia.prescrittore}
                </p>
              </div>
            ))
          )}
        </div>
      </Section>

      <div className="mt-8 bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <div className="flex justify-between items-center px-4">
            <div className="flex justify-between items-center w-full">
              <div className="flex">
                {Object.keys(tabTagMapping).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab as TabType)}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === tab
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              <button
                onClick={handleSortToggle}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {sortDirection === "DESC"
                  ? "Più recenti prima"
                  : "Più vecchi prima"}
              </button>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="divide-y">
            {filteredDocuments.map((doc) => (
              <DocumentTab
                key={doc.id}
                doc={doc}
                onNavigate={handleNavigate}
                activeTab={activeTab}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientProfile;
